<template>
    <v-container class="pa-2">
        <v-row dense>
            <v-col cols="12" sm="8" offset-sm="2" md="6" offset-md="3">

                <!-- Header -->
                <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3">
                    <div class="d-flex align-center">
                        <div class="text-subtitle-2 font-weight-bold primary--text flex-grow-1">Impostazioni</div>
                        <v-icon color="primary" size="22">mdi-cog-outline</v-icon>
                    </div>
                </v-card>

                <!-- Profilo -->
                <v-card class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-account-outline</v-icon>
                        <span>Profilo</span>
                    </div>
                    <v-card-text class="pt-0 pb-3 px-3">
                        <div class="d-flex align-center mb-3">
                            <v-avatar size="48" color="primary" class="mr-3 flex-shrink-0">
                                <span class="white--text font-weight-bold" style="font-size:1rem">
                                    {{ initiali }}
                                </span>
                            </v-avatar>
                            <div class="flex-grow-1">
                                <div class="text-body-2 font-weight-bold">{{ nominativo }}</div>
                                <div class="text-caption grey--text">{{ congregazioneNome }}</div>
                                <div class="d-flex flex-wrap mt-1" style="gap:4px">
                                    <span v-if="oratore.isAnziano"      class="ruolo-badge" style="background:#009688">A</span>
                                    <span v-if="oratore.isSM"           class="ruolo-badge" style="background:#1976D2">SM</span>
                                    <span v-if="oratore.isAssistente"   class="ruolo-badge" style="background:#616161">ASSISTENTE</span>
                                    <span v-if="oratore.isCoordinatore" class="ruolo-badge" style="background:#BF360C">ADM</span>
                                    <span v-if="oratore.isAdmin"        class="ruolo-badge" style="background:#F57C00">APP-ADM</span>
                                    <span v-if="isSuperAdmin"           class="ruolo-badge" style="background:#6A1B9A">SUPER-ADMIN</span>
                                </div>
                            </div>
                        </div>

                        <!-- Email (se presente) -->
                        <div v-if="oratore.mail" class="d-flex align-center mb-1">
                            <v-icon x-small color="grey" class="mr-2">mdi-email-outline</v-icon>
                            <span class="text-caption grey--text flex-grow-1">Email: <b>{{ oratore.mail }}</b></span>
                        </div>

                        <!-- Codice collaboratore -->
                        <div class="d-flex align-center">
                            <v-icon x-small color="grey" class="mr-2">mdi-key-outline</v-icon>
                            <span class="text-caption grey--text flex-grow-1">Codice: <b>{{ oratore.codice }}-{{ $store.state.partition }}</b></span>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Sezioni visibili solo agli utenti normali (non al collaboratore) -->
                <template v-if="!isCollaboratore">

                <!-- Circoscrizione -->
                <v-card class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-domain</v-icon>
                        <span>Circoscrizione</span>
                    </div>
                    <v-card-text class="pt-4 pb-3 px-3">
                        <template v-if="isAdminOrSuper">
                            <v-text-field v-model="circInfo.nome"
                                outlined rounded dense hide-details
                                label="Circoscrizione"
                                prepend-inner-icon="mdi-domain"
                                class="mb-2" />
                            <v-text-field v-model="circInfo.sorvegliante"
                                outlined rounded dense hide-details
                                label="Nome Sorvegliante"
                                prepend-inner-icon="mdi-account-tie-outline"
                                class="mb-3" />
                            <div class="d-flex justify-end">
                                <v-btn small rounded depressed color="primary"
                                    :loading="loadingSalvaCircInfo"
                                    @click="salvaCircInfo">
                                    Salva
                                </v-btn>
                            </div>
                        </template>
                        <template v-else>
                            <div class="d-flex align-center mb-2">
                                <v-icon small color="grey" class="mr-2">mdi-domain</v-icon>
                                <span class="text-caption grey--text mr-2">Circoscrizione</span>
                                <span class="text-body-2 font-weight-medium">{{ circInfo.nome || '—' }}</span>
                            </div>
                            <div class="d-flex align-center">
                                <v-icon small color="grey" class="mr-2">mdi-account-tie-outline</v-icon>
                                <span class="text-caption grey--text mr-2">Nome Sorvegliante</span>
                                <span class="text-body-2 font-weight-medium">{{ circInfo.sorvegliante || '—' }}</span>
                            </div>
                        </template>
                    </v-card-text>
                </v-card>

                <!-- Codice Collaboratore -->
                <v-card v-if="puoVedereCodiceCollab" class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-account-key-outline</v-icon>
                        <span>Codice Collaboratore</span>
                    </div>
                    <v-card-text class="pt-0 pb-3 px-3">
                        <div class="text-caption grey--text mb-3">
                            Questo codice è impersonale e può essere fornito a un collaboratore per visualizzare la sola programmazione interna della congregazione, al fine di avvisare gli oratori in visita.
                        </div>
                        <div class="d-flex align-center coll-code-box pa-2 rounded-lg mb-1" style="cursor:pointer" @click="copiaCollaboratore">
                            <div class="flex-grow-1">
                                <div class="text-caption grey--text">Codice accesso collaboratore</div>
                                <div class="text-body-2 font-weight-bold font-mono">{{ collaboratore.codice || '—' }}</div>
                            </div>
                            <v-btn icon small color="primary">
                                <v-icon small>mdi-content-copy</v-icon>
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Disponibilità -->
                <v-card class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-calendar-check-outline</v-icon>
                        <span>Disponibilità</span>
                    </div>
                    <div class="d-flex align-center px-3 pb-3">
                        <div class="flex-grow-1">
                            <div class="text-body-2">Disponibile come oratore di riserva</div>
                            <div class="text-caption grey--text">Sarai disponibile per le sostituzioni dell'ultimo minuto</div>
                        </div>
                        <v-switch v-model="isRiserva" inset dense hide-details class="mt-0 pt-0"
                            @change="salvaRiserva" />
                    </div>
                </v-card>

                <!-- Donazione PayPal -->
                <v-card v-if="paypalEnabled" class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-heart-outline</v-icon>
                        <span>Supporta l'app</span>
                    </div>
                    <v-card-text class="pt-2 pb-3 px-3">
                        <div class="text-caption grey--text mb-3">
                            Talk App è gratuita e senza pubblicità. Se ti è utile, puoi supportarne lo sviluppo con una piccola donazione.
                        </div>
                        <v-btn depressed rounded color="#0070ba" dark small @click="apriPaypal">
                            Dona con PayPal
                        </v-btn>
                    </v-card-text>
                </v-card>

                <!-- Sicurezza -->
                <v-card class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-shield-lock-outline</v-icon>
                        <span>Sicurezza</span>
                    </div>
                    <v-list dense class="pa-0">
                        <v-list-item dense @click="apriDialogMail" class="rounded-0">
                            <v-list-item-icon class="mr-3 my-auto">
                                <v-avatar size="30" color="blue darken-1">
                                    <v-icon small dark>mdi-email-edit-outline</v-icon>
                                </v-avatar>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="text-body-2">Aggiorna Email</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-icon color="grey lighten-1" size="18">mdi-chevron-right</v-icon>
                            </v-list-item-action>
                        </v-list-item>
                        <v-divider />
                        <v-list-item dense @click="dialogPassword = true" class="rounded-b-xl">
                            <v-list-item-icon class="mr-3 my-auto">
                                <v-avatar size="30" color="orange darken-1">
                                    <v-icon small dark>mdi-lock-reset</v-icon>
                                </v-avatar>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="text-body-2">Cambia Password</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-icon color="grey lighten-1" size="18">mdi-chevron-right</v-icon>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </v-card>

                <!-- Aspetto -->
                <v-card class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-palette-outline</v-icon>
                        <span>Aspetto</span>
                    </div>
                    <div class="d-flex align-center px-3 pb-3">
                        <v-avatar size="30" color="blue darken-1" class="mr-3 flex-shrink-0">
                            <v-icon small dark>mdi-theme-light-dark</v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                            <div class="text-body-2">Tema Scuro</div>
                        </div>
                        <v-switch v-model="themeDark" inset dense hide-details class="mt-0 pt-0"
                            @change="switchTheme" />
                    </div>
                </v-card>

                <!-- Amministratori App (visibile a tutti) -->
                <v-card class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-shield-account-outline</v-icon>
                        <span>Amministratori App</span>
                    </div>
                    <v-card-text class="pt-0 pb-3 px-3">
                        <div v-if="loadingAppAdmin" class="d-flex justify-center py-2">
                            <v-progress-circular indeterminate color="orange darken-1" size="20" width="2" />
                        </div>
                        <div v-else-if="listaAppAdmin.length === 0" class="text-caption grey--text">
                            Nessun amministratore configurato
                        </div>
                        <div v-else>
                            <div v-for="(admin, i) in listaAppAdmin" :key="admin.id"
                                 class="d-flex align-center py-2"
                                 :style="{ gap: '10px', borderTop: i > 0 ? '1px solid rgba(128,128,128,0.2)' : 'none' }">
                                    <v-avatar size="32" color="orange darken-1" class="flex-shrink-0">
                                        <span class="white--text" style="font-size:0.6rem;font-weight:700">
                                            {{ admin.nominativo.split(' ').map(w => w[0]).slice(0,2).join('').toUpperCase() }}
                                        </span>
                                    </v-avatar>
                                    <div class="flex-grow-1">
                                        <div class="text-body-2 font-weight-medium">{{ admin.nominativo }}</div>
                                        <div class="d-flex align-center flex-wrap text-caption grey--text" style="gap:8px">
                                            <span><v-icon x-small color="grey" class="mr-1">mdi-home-city-outline</v-icon>{{ admin.congregazioneNome || '—' }}</span>
                                            <span v-if="admin.telefono"
                                                  style="cursor:pointer"
                                                  @click="$openUrl('tel:' + admin.telefono.replace(/\s/g, ''))">
                                                <v-icon x-small color="primary" class="mr-1">mdi-phone-outline</v-icon>{{ admin.telefono }}
                                            </span>
                                        </div>
                                    </div>
                                    <span class="ruolo-badge" style="background:#F57C00">APP-ADM</span>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Admin: Circoscrizione -->
                <v-card v-if="isAdminOrSuper" class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-tune</v-icon>
                        <span>Amministrazione</span>
                        <v-chip x-small color="orange darken-1" dark class="ml-2">APP-ADM</v-chip>
                    </div>
                    <v-card-text class="pt-0 pb-2 px-3">

                        <!-- Prenotazioni -->
                        <div class="setting-group-label">Prenotazioni</div>

                        <div class="d-flex align-center mb-2">
                            <div class="flex-grow-1">
                                <div class="text-body-2 font-weight-medium">Attiva meccanismo di prenotazione</div>
                                <div class="text-caption grey--text">Abilita il sistema di prenotazioni per la circoscrizione</div>
                            </div>
                            <v-switch v-model="circImpost.prenotazione"
                                inset dense hide-details class="mt-0 pt-0" />
                        </div>

                        <template v-if="circImpost.prenotazione">
                        <div class="d-flex align-center mb-1">
                            <div class="flex-grow-1">
                                <div class="text-body-2">Prenotazione obbligatoria dopo 15gg</div>
                                <div class="text-caption grey--text">Richiede conferma automatica passati 15 giorni</div>
                            </div>
                            <v-switch v-model="circImpost.prenotazioneObbligatoria"
                                inset dense hide-details class="mt-0 pt-0" />
                        </div>

                        <div class="d-flex align-center mb-1 mt-1">
                            <div class="text-body-2 flex-grow-1">Giorni scadenza prenotazione</div>
                            <div class="d-flex align-center ml-2" style="gap:4px">
                                <v-btn icon x-small :disabled="circImpost.giorniScadenzaPrenotazione <= giorniOptions[0]"
                                    @click="stepGiorni(-1)">
                                    <v-icon small>mdi-minus</v-icon>
                                </v-btn>
                                <span class="text-body-2 font-weight-bold" style="min-width:28px;text-align:center">
                                    {{ circImpost.giorniScadenzaPrenotazione }}
                                </span>
                                <v-btn icon x-small :disabled="circImpost.giorniScadenzaPrenotazione >= giorniOptions[giorniOptions.length - 1]"
                                    @click="stepGiorni(1)">
                                    <v-icon small>mdi-plus</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <!-- Congregazioni con prenotazione -->
                        <div class="d-flex align-center py-2 mt-1" style="cursor:pointer" @click="dialogCongPrenotazione = true">
                            <v-icon x-small color="primary" class="mr-2">mdi-home-city-outline</v-icon>
                            <span class="text-body-2 flex-grow-1 primary--text">Congregazioni con prenotazione</span>
                            <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
                        </div>
                        </template>

                        <!-- Salva / Annulla prenotazioni (visibili solo se modificate) -->
                        <v-expand-transition>
                            <div v-if="prenotazioniDirty" class="d-flex justify-end mt-2" style="gap:8px">
                                <v-btn small rounded text color="grey darken-1"
                                    @click="annullaPrenotazioni">
                                    Annulla
                                </v-btn>
                                <v-btn small rounded depressed color="primary"
                                    :loading="loadingSalvaAdmin"
                                    @click="salvaImpostazioniAdmin">

                                    Salva
                                </v-btn>
                            </div>
                        </v-expand-transition>

                        <v-divider class="my-2" />

                        <!-- Rotazione -->
                        <div class="setting-group-label">Rotazione</div>

                        <div class="d-flex align-center mb-1">
                            <div class="flex-grow-1">
                                <div class="text-body-2">Attiva warning di Rotazione</div>
                                <div class="text-caption grey--text">Avvisa se un oratore non ruota abbastanza</div>
                            </div>
                            <v-switch v-model="circImpost.attivRotazione"
                                inset dense hide-details class="mt-0 pt-0"
                                @change="salvaImpostazioniAdmin" />
                        </div>

                        <div v-if="circImpost.attivRotazione" class="d-flex align-center mb-1 mt-1">
                            <div class="flex-grow-1">
                                <div class="text-body-2">Congregazioni da ruotare</div>
                                <div class="text-caption grey--text">Esclude congregazione di appartenenza e quella di arrivo</div>
                            </div>
                            <v-select
                                v-model="circImpost.numeroCongRotazione"
                                :items="congregazioniRotazioneOptions"
                                outlined rounded dense hide-details
                                style="max-width:90px"
                                class="ml-2"
                                @change="salvaImpostazioniAdmin" />
                        </div>

                        <v-divider class="my-2" />

                        <!-- Data massima appuntamenti -->
                        <div class="setting-group-label">Programma</div>

                        <v-card outlined rounded class="mb-3 pa-3">
                            <div class="d-flex align-center mb-2">
                                <v-icon color="primary" class="mr-2">mdi-calendar-lock-outline</v-icon>
                                <div>
                                    <div class="text-body-2 font-weight-medium">Limite massimo appuntamenti</div>
                                    <div class="text-caption grey--text">Non sarà possibile fissare nuovi appuntamenti oltre la data limite</div>
                                </div>
                            </div>

                            <!-- Toggle modo -->
                            <v-btn-toggle v-model="modoDataMax" mandatory dense rounded class="mb-3" color="primary">
                                <v-btn value="fissa" x-small>
                                    <v-icon x-small left>mdi-calendar</v-icon>Data fissa
                                </v-btn>
                                <v-btn value="mesi" x-small>
                                    <v-icon x-small left>mdi-calendar-month</v-icon>N. mesi
                                </v-btn>
                            </v-btn-toggle>

                            <!-- Modalità data fissa -->
                            <div v-if="modoDataMax === 'fissa'" class="d-flex align-center" style="gap:8px">
                                <v-text-field
                                    v-model="circImpost.dataMaxAppuntamenti"
                                    type="date"
                                    outlined rounded dense hide-details
                                    label="Data massima"
                                    style="max-width:200px" />
                                <v-btn icon x-small color="grey"
                                    :disabled="!circImpost.dataMaxAppuntamenti"
                                    @click="circImpost.dataMaxAppuntamenti = null">
                                    <v-icon small>mdi-close-circle-outline</v-icon>
                                </v-btn>
                            </div>

                            <!-- Modalità n. mesi -->
                            <div v-else>
                                <div class="d-flex align-center mb-2" style="gap:8px">
                                    <v-text-field
                                        v-model.number="circImpost.mesiMaxAppuntamenti"
                                        type="number" min="1" max="36"
                                        outlined rounded dense hide-details
                                        label="Numero di mesi"
                                        style="max-width:150px" />
                                    <v-btn icon x-small color="grey"
                                        :disabled="!circImpost.mesiMaxAppuntamenti"
                                        @click="circImpost.mesiMaxAppuntamenti = 0">
                                        <v-icon small>mdi-close-circle-outline</v-icon>
                                    </v-btn>
                                </div>
                                <!-- Preview data calcolata -->
                                <div v-if="circImpost.mesiMaxAppuntamenti > 0" class="d-flex align-center" style="gap:6px">
                                    <v-icon small color="grey">mdi-information-outline</v-icon>
                                    <span class="text-caption grey--text">
                                        Data limite calcolata:
                                        <strong class="primary--text">{{ dataMaxCalcolata }}</strong>
                                    </span>
                                </div>
                            </div>

                            <div class="d-flex justify-end mt-3">
                                <v-btn small rounded depressed color="primary"
                                    :loading="loadingSalvaDataMax"
                                    @click="salvaDataMaxAppuntamenti">
                                    Salva
                                </v-btn>
                            </div>
                        </v-card>

                        <v-divider class="my-2" />

                        <!-- Impersonifica -->
                        <div class="setting-group-label">Strumenti</div>

                        <v-autocomplete
                            v-model="oratoreImpersonificato"
                            :items="listaOratori"
                            :item-text="o => o.nominativo + (o.congregazioneNome ? ' — ' + o.congregazioneNome : '')"
                            item-value="id"
                            outlined rounded dense hide-details clearable
                            label="Impersonifica oratore"
                            prepend-inner-icon="mdi-account-switch-outline"
                            no-data-text="Nessun oratore trovato"
                            class="mb-2"
                            @change="selezionaImpersonifica" />

                        <v-divider class="mt-2 mb-2" />

                        <!-- Elimina dati app -->
                        <div class="d-flex align-center py-1">
                            <v-avatar size="30" color="red darken-2" class="mr-3 flex-shrink-0">
                                <v-icon small dark>mdi-database-remove-outline</v-icon>
                            </v-avatar>
                            <div class="flex-grow-1">
                                <div class="text-body-2">Elimina Dati App</div>
                                <div class="text-caption grey--text">Rimuove tutti i dati della circoscrizione in modo definitivo</div>
                            </div>
                            <v-btn icon x-small color="error" @click="dialogEliminaDati = true">
                                <v-icon small>mdi-delete-outline</v-icon>
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Super Admin -->
                <v-card v-if="false" class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-wrench-outline</v-icon>
                        <span>Sistema</span>
                        <v-chip x-small color="deep-purple darken-2" dark class="ml-2">SUPER</v-chip>
                    </div>
                    <v-card-text class="pt-0 pb-2 px-3">
                        <div class="d-flex flex-wrap" style="gap:8px">
                            <v-btn x-small depressed rounded color="info" :loading="loadingOp === 'notifica'"
                                @click="azioneAdmin('notifica')">
                                <v-icon x-small left>mdi-bell-ring-outline</v-icon>
                                Notifica Discorsi
                            </v-btn>
                            <v-btn x-small depressed rounded color="secondary" :loading="loadingOp === 'inactive'"
                                @click="azioneAdmin('inactive')">
                                <v-icon x-small left>mdi-account-clock-outline</v-icon>
                                Lista Inactive
                            </v-btn>
                            <v-btn x-small depressed rounded color="teal" dark :loading="loadingOp === 'sana'"
                                @click="azioneAdmin('sana')">
                                <v-icon x-small left>mdi-auto-fix</v-icon>
                                Sana Codici
                            </v-btn>
                            <v-btn x-small depressed rounded color="orange darken-2" dark :loading="loadingOp === 'oscura'"
                                @click="azioneAdmin('oscura')">
                                <v-icon x-small left>mdi-eye-off-outline</v-icon>
                                Oscura Codici
                            </v-btn>
                            <v-btn x-small depressed rounded color="success" :loading="loadingOp === 'ripristina'"
                                @click="azioneAdmin('ripristina')">
                                <v-icon x-small left>mdi-eye-outline</v-icon>
                                Ripristina Codici
                            </v-btn>
                            <v-btn x-small depressed rounded color="primary" :loading="loadingOp === 'refresh'"
                                @click="azioneAdmin('refresh')">
                                <v-icon x-small left>mdi-refresh</v-icon>
                                Refresh
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Applicazione (solo app nativa) -->
                <v-card v-if="isNativeApp" class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-update</v-icon>
                        <span>Applicazione</span>
                    </div>
                    <v-list dense class="pa-0">
                        <v-list-item dense @click="checkUpdate" :disabled="updating" class="rounded-b-xl">
                            <v-list-item-icon class="mr-3 my-auto">
                                <v-avatar size="30" color="teal">
                                    <v-progress-circular v-if="updating" indeterminate size="20" color="white" />
                                    <v-icon v-else small dark>mdi-update</v-icon>
                                </v-avatar>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="text-body-2">Verifica aggiornamenti</v-list-item-title>
                                <v-list-item-subtitle class="text-caption">{{ updateStatus }}</v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-icon color="grey lighten-1" size="18">mdi-chevron-right</v-icon>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </v-card>

                <!-- OneSignal Shard (solo superadmin) -->
                <v-card v-if="isSuperAdmin" class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-bell-ring-outline</v-icon>
                        <span>OneSignal</span>
                        <v-chip x-small color="deep-purple darken-2" dark class="ml-2">SUPER</v-chip>
                    </div>
                    <v-card-text class="pt-2 pb-3 px-3">
                        <div class="d-flex align-center mb-1">
                            <v-icon x-small color="grey" class="mr-2">mdi-server-outline</v-icon>
                            <span class="text-caption grey--text mr-1">Shard:</span>
                            <v-chip x-small color="deep-purple darken-2" dark class="font-weight-bold">{{ oneSignalShard }}</v-chip>
                        </div>
                        <div class="d-flex align-center">
                            <v-icon x-small color="grey" class="mr-2">mdi-map-marker-outline</v-icon>
                            <span class="text-caption grey--text mr-1">Partition:</span>
                            <span class="text-caption font-weight-medium">{{ $store.state.partition }}</span>
                        </div>
                    </v-card-text>
                </v-card>

                </template><!-- /!isCollaboratore -->

                <!-- Footer / Logout -->
                <v-card class="rounded-xl elevation-2 mb-3">
                    <v-list dense class="pa-0">
                        <v-list-item dense @click="dialogPrivacy = true">
                            <v-list-item-icon class="mr-3 my-auto">
                                <v-avatar size="30" color="blue-grey">
                                    <v-icon small dark>mdi-shield-account-outline</v-icon>
                                </v-avatar>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="text-body-2">Privacy e Dati</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-icon color="grey lighten-1" size="18">mdi-chevron-right</v-icon>
                            </v-list-item-action>
                        </v-list-item>
                        <v-divider />
                        <v-list-item dense @click="logout">
                            <v-list-item-icon class="mr-3 my-auto">
                                <v-avatar size="30" color="error">
                                    <v-icon small dark>mdi-logout</v-icon>
                                </v-avatar>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="text-body-2 error--text">Esci</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card>

                <!-- Versione -->
                <div class="text-center text-caption grey--text py-2 mb-2">
                    <div>Talk · Client VRM {{ applicationVersion }}</div>
                    <div v-if="$store.state.oratore.isAdmin">Admin</div>
                    <div>Lpl Soft &copy; {{ new Date().getFullYear() }}</div>
                </div>

            </v-col>
        </v-row>

        <!-- Dialog conferma impersonifica -->
        <v-dialog v-model="dialogImpersonifica" max-width="360px">
            <v-card v-if="oratorePreview" class="rounded-xl elevation-24">
                <v-card-title class="text-subtitle-1 font-weight-bold px-4 pt-4 pb-2">
                    Accedi come
                </v-card-title>
                <v-card-text class="px-4 pb-3">
                    <div class="d-flex align-center mb-3" style="gap:12px">
                        <v-avatar size="48" color="primary">
                            <span class="white--text font-weight-bold" style="font-size:1rem">
                                {{ oratorePreview.nominativo.split(' ').map(w => w[0]).slice(0,2).join('').toUpperCase() }}
                            </span>
                        </v-avatar>
                        <div>
                            <div class="text-body-1 font-weight-bold">{{ oratorePreview.nominativo }}</div>
                            <div class="text-caption grey--text">{{ oratorePreview.congregazioneNome }}</div>
                            <div class="d-flex flex-wrap mt-1" style="gap:4px">
                                <span v-if="oratorePreview.isAnziano"      class="ruolo-badge" style="background:#009688">A</span>
                                <span v-if="oratorePreview.isSM"           class="ruolo-badge" style="background:#1976D2">SM</span>
                                <span v-if="oratorePreview.isAssistente"   class="ruolo-badge" style="background:#616161">ASSISTENTE</span>
                                <span v-if="oratorePreview.isCoordinatore" class="ruolo-badge" style="background:#BF360C">ADM</span>
                                <span v-if="oratorePreview.isAdmin"        class="ruolo-badge" style="background:#F57C00">APP-ADM</span>
                            </div>
                        </div>
                    </div>
                    <div class="text-caption grey--text">
                        Accederai all'app con le credenziali e i permessi di questo oratore.
                    </div>
                </v-card-text>
                <v-divider />
                <v-card-actions class="px-4 py-3">
                    <v-btn text rounded @click="annullaImpersonifica">Annulla</v-btn>
                    <v-spacer />
                    <v-btn color="primary" rounded depressed
                        :loading="loadingImpersonifica"
                        @click="confermaImpersonifica">
                        <v-icon small left>mdi-account-switch-outline</v-icon>
                        Accedi
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog congregazioni con prenotazione -->
        <v-dialog v-model="dialogCongPrenotazione" max-width="480px">
            <v-card class="rounded-xl elevation-24">
                <v-card-title class="text-subtitle-1 font-weight-bold px-4 pt-4 pb-2">
                    Congregazioni con prenotazione
                </v-card-title>
                <v-card-text class="px-2 pb-2">
                    <div v-if="loadingCongPrenotazione" class="pa-4 text-center">
                        <v-progress-circular indeterminate color="primary" />
                    </div>
                    <v-list dense v-else>
                        <v-list-item v-for="item in congregazioniPrenotazione" :key="item.id" dense>
                            <v-list-item-content>
                                <v-list-item-title class="text-body-2">{{ item.nome }}</v-list-item-title>
                                <v-list-item-subtitle v-if="item.coordinatore" class="text-caption d-flex align-center" style="gap:6px">
                                    <v-icon x-small color="grey">mdi-account-tie-outline</v-icon>
                                    {{ item.coordinatore.nominativo }}
                                    <span class="ruolo-badge" style="background:#BF360C">ADM</span>
                                    <chip-login-oratore :oratore="item.coordinatore" />
                                </v-list-item-subtitle>
                                <v-list-item-subtitle v-for="ass in item.assistenti" :key="ass.nominativo"
                                    class="text-caption d-flex align-center" style="gap:6px">
                                    <v-icon x-small color="grey">mdi-account-outline</v-icon>
                                    {{ ass.nominativo }}
                                    <span class="ruolo-badge" style="background:#616161">ASSISTENTE</span>
                                    <chip-login-oratore :oratore="ass" />
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-switch v-model="item.prenotazione" inset dense hide-details class="mt-0 pt-0" />
                            </v-list-item-action>
                        </v-list-item>
                        <div v-if="congregazioniPrenotazione.length === 0" class="text-center grey--text py-4 text-caption">
                            Nessuna congregazione disponibile
                        </div>
                    </v-list>
                </v-card-text>
                <v-divider />
                <v-card-actions class="px-4 py-3">
                    <v-btn text rounded color="grey darken-1"
                        @click="annullaCongPrenotazione">
                        Chiudi
                    </v-btn>
                    <v-spacer />
                    <v-btn color="primary" rounded depressed
                        :loading="loadingSalvaCong"
                        :disabled="!congPrenotazioneDirty"
                        @click="salvaTutteCongPrenotazione">
                        <v-icon x-small left>mdi-content-save-outline</v-icon>
                        Salva
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog aggiorna credenziali (email + password) -->
        <v-dialog v-model="dialogMail" max-width="420px" persistent>
            <v-card class="rounded-xl elevation-24">
                <v-card-title class="text-subtitle-1 font-weight-bold px-4 pt-4 pb-2">
                    Aggiorna Credenziali
                </v-card-title>
                <v-card-text class="px-4 pb-2">
                    <v-form v-model="validMail" ref="formMail">
                        <v-text-field
                            v-model="mailForm.nuova"
                            outlined rounded dense
                            label="Email"
                            prepend-inner-icon="mdi-email-outline"
                            autocapitalize="none"
                            :rules="[v => !!v || 'campo obbligatorio', v => /.+@.+\..+/.test(v) || 'email non valida', () => !mailDuplicataError || 'email già associata ad un altro account']"
                            :error="mailDuplicataError"
                            @input="mailDuplicataError = false"
                            class="mb-2" />
                        <v-text-field
                            v-model="mailForm.password"
                            outlined rounded dense
                            label="Nuova password (opzionale)"
                            prepend-inner-icon="mdi-lock-reset"
                            :type="showMailPwd ? 'text' : 'password'"
                            :append-icon="showMailPwd ? 'mdi-eye-off' : 'mdi-eye'"
                            @click:append="showMailPwd = !showMailPwd"
                            :rules="[v => !v || v.length >= 4 || 'minimo 4 caratteri']"
                            class="mb-2" />
                        <v-text-field
                            v-if="mailForm.password"
                            v-model="mailForm.conferma"
                            outlined rounded dense
                            label="Conferma nuova password"
                            prepend-inner-icon="mdi-lock-check-outline"
                            :type="showMailPwd ? 'text' : 'password'"
                            :rules="[v => !!v || 'campo obbligatorio', v => v === mailForm.password || 'le password non coincidono']"
                            class="mb-1" />
                    </v-form>
                </v-card-text>
                <v-divider />
                <v-card-actions class="px-4 py-3">
                    <v-btn text rounded @click="chiudiDialogMail">Annulla</v-btn>
                    <v-spacer />
                    <v-btn color="primary" rounded depressed
                        :disabled="!validMail" :loading="loadingMail"
                        @click="aggiornaMail">
                        Salva
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog cambio password -->
        <v-dialog v-model="dialogPassword" max-width="420px" persistent>
            <v-card class="rounded-xl elevation-24">
                <v-card-title class="text-subtitle-1 font-weight-bold px-4 pt-4 pb-2">
                    Cambia Password
                </v-card-title>
                <v-card-text class="px-4 pb-2">
                    <v-form v-model="validPwd" ref="formPassword">
                        <v-text-field
                            v-model="pwdForm.vecchia"
                            outlined rounded dense
                            label="Password attuale"
                            prepend-inner-icon="mdi-lock-outline"
                            :type="showPwd ? 'text' : 'password'"
                            :append-icon="showPwd ? 'mdi-eye-off' : 'mdi-eye'"
                            @click:append="showPwd = !showPwd"
                            :rules="[v => !!v || 'campo obbligatorio']"
                            :error-messages="pwdErrata ? 'Password attuale non corretta' : ''"
                            @input="pwdErrata = false"
                            class="mb-2" />
                        <v-text-field
                            v-model="pwdForm.nuova"
                            outlined rounded dense
                            label="Nuova password"
                            prepend-inner-icon="mdi-lock-reset"
                            :type="showPwd2 ? 'text' : 'password'"
                            :append-icon="showPwd2 ? 'mdi-eye-off' : 'mdi-eye'"
                            @click:append="showPwd2 = !showPwd2"
                            :rules="[v => !!v || 'campo obbligatorio', v => v.length >= 4 || 'minimo 4 caratteri']"
                            class="mb-2" />
                        <v-text-field
                            v-model="pwdForm.conferma"
                            outlined rounded dense
                            label="Conferma nuova password"
                            prepend-inner-icon="mdi-lock-check-outline"
                            :type="showPwd2 ? 'text' : 'password'"
                            :rules="[v => !!v || 'campo obbligatorio', v => v === pwdForm.nuova || 'le password non coincidono']"
                            class="mb-1" />
                    </v-form>
                </v-card-text>
                <v-divider />
                <v-card-actions class="px-4 py-3">
                    <v-btn text rounded @click="chiudiDialogPassword">Annulla</v-btn>
                    <v-spacer />
                    <v-btn color="primary" rounded depressed
                        :disabled="!validPwd" :loading="loadingPwd"
                        @click="cambiaPassword">

                        Salva
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog elimina dati - primo step -->
        <v-dialog v-model="dialogEliminaDati" max-width="400px">
            <v-card class="rounded-xl elevation-24">
                <v-card-title class="text-subtitle-1 font-weight-bold px-4 pt-4 pb-2 error--text">
                    <v-icon color="error" left>mdi-alert</v-icon>
                    Elimina Dati App
                </v-card-title>
                <v-card-text class="px-4 pb-2">
                    Questa operazione rimuoverà <strong>tutti i dati</strong> della circoscrizione in modo definitivo e irreversibile. Sei sicuro?
                </v-card-text>
                <v-divider />
                <v-card-actions class="px-4 py-3">
                    <v-btn text rounded @click="dialogEliminaDati = false">Annulla</v-btn>
                    <v-spacer />
                    <v-btn color="error" rounded depressed
                        @click="apriSecondaConferma">
                        <v-icon left small>mdi-delete</v-icon>
                        Elimina
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog elimina dati - secondo step -->
        <v-dialog v-model="dialogEliminaConferma" max-width="400px">
            <v-card class="rounded-xl elevation-24">
                <v-card-title class="text-subtitle-1 font-weight-bold px-4 pt-4 pb-2 error--text">
                    <v-icon color="error" left>mdi-alert-octagon</v-icon>
                    Conferma eliminazione
                </v-card-title>
                <v-card-text class="px-4 pb-2">
                    <strong>Ultima conferma.</strong> Tutti i dati verranno cancellati e non sarà possibile recuperarli. Confermi l'eliminazione definitiva?
                </v-card-text>
                <v-divider />
                <v-card-actions class="px-4 py-3">
                    <v-btn text rounded @click="dialogEliminaConferma = false">Annulla</v-btn>
                    <v-spacer />
                    <v-btn color="error" rounded depressed :loading="loadingEliminaDati"
                        @click="eliminaDati">
                        <v-icon left small>mdi-delete-forever</v-icon>
                        Elimina definitivamente
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <privacy-dialog v-model="dialogPrivacy" />

        <!-- Snackbars -->
        <v-snackbar v-model="snackSuccess" timeout="2500" color="success" bottom>
            <v-icon dark class="mr-2">mdi-checkbox-marked-circle</v-icon>
            {{ snackMsg || 'Operazione eseguita con successo.' }}
        </v-snackbar>
        <v-snackbar v-model="snackError" timeout="2500" color="error" bottom>
            <v-icon dark class="mr-2">mdi-alert-circle</v-icon>
            Errore durante l'esecuzione dell'operazione.
        </v-snackbar>
        <v-snackbar v-model="snackInfo" timeout="2000" color="info" bottom>
            <v-icon dark class="mr-2">mdi-content-copy</v-icon>
            Codice copiato negli appunti.
        </v-snackbar>
    </v-container>
</template>

<script>
import { callService, callPost, SUPERADMIN_CODE, logout, gDataCalendar } from '@/modules/utilities.js'
import { openUrl } from '@/modules/mauiBridge.js'
import { version } from '/package.json'
import PrivacyDialog from '@/components/PrivacyDialog.vue'
import ChipLoginOratore from '@/components/ChipLoginOratore.vue'

/**
 * Replica di Mono string.GetHashCode() — stesso algoritmo usato dall'app Xamarin
 * per generare il codice collaboratore: Math.Abs(("C" + oratore.OrId).GetHashCode())
 */
function monoStringHashCode(str) {
    let h = 0;
    const len = str.length;
    let i = 0;
    while (i < len - 1) {
        h = Math.imul(h, 31) + str.charCodeAt(i);
        h = (Math.imul(h, 31) + str.charCodeAt(i + 1)) | 0;
        i += 2;
    }
    if (i < len) {
        h = (Math.imul(h, 31) + str.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
}

export default {
    components: { PrivacyDialog, ChipLoginOratore },
    data: () => ({
        applicationVersion: version,
        SUPERADMIN_CODE: '',
        isRiserva: false,
        themeDark: false,
        dialogPassword: false,
        dialogEliminaDati: false,
        dialogEliminaConferma: false,
        dialogPrivacy: false,
        dialogCongPrenotazione: false,
        congregazioniPrenotazione: [],
        loadingCongPrenotazione: false,
        loadingSalvaCong: false,
        congregazioniPrenotazioneSnap: [],
        dialogMail: false,
        validMail: false,
        loadingMail: false,
        mailForm: { nuova: '', password: '', conferma: '' },
        mailDuplicataError: false,
        showMailPwd: false,
        validPwd: false,
        loadingPwd: false,
        loadingSalvaAdmin: false,
        loadingSalvaDataMax: false,
        modoDataMax: 'fissa', // 'fissa' | 'mesi'
        loadingEliminaDati: false,
        loadingOp: '',
        showPwd: false,
        showPwd2: false,
        pwdForm: { vecchia: '', nuova: '', conferma: '' },
        pwdErrata: false,
        circImpost: {
            prenotazione: false,
            prenotazioneObbligatoria: false,
            giorniScadenzaPrenotazione: 15,
            attivRotazione: false,
            numeroCongRotazione: 3,
            dataMaxAppuntamenti: null,
            mesiMaxAppuntamenti: 0,
        },
        circImpostSnap: {
            prenotazione: false,
            prenotazioneObbligatoria: false,
            giorniScadenzaPrenotazione: 15,
        },
        circInfo: { nome: '', sorvegliante: '' },
        loadingSalvaCircInfo: false,
        collaboratore: { codice: '', password: '' },
        listaOratori: [],
        listaAppAdmin: [],
        loadingAppAdmin: false,
        oratoreImpersonificato: null,
        dialogImpersonifica: false,
        oratorePreview: null,
        loadingImpersonifica: false,
        isNativeApp: false,
        updating: false,
        updateStatus: 'Controlla se è disponibile una nuova versione',
        snackSuccess: false,
        snackError: false,
        snackInfo: false,
        snackMsg: '',
        giorniOptions: [7, 10, 14, 15, 20, 30],
        congregazioniRotazioneOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    }),
    computed: {
        // Calcola la data massima in base al numero di mesi impostato:
        // N mesi a partire dall'inizio del mese successivo, ultimo giorno incluso.
        // Esempio: oggi 6 ott, 6 mesi → 30 apr dell'anno successivo
        dataMaxCalcolata() {
            const n = parseInt(this.circImpost.mesiMaxAppuntamenti) || 0;
            if (n <= 0) return null;
            const oggi = new Date();
            const inizioMeseProssimo = new Date(oggi.getFullYear(), oggi.getMonth() + 1, 1);
            const fine = new Date(inizioMeseProssimo);
            fine.setMonth(fine.getMonth() + n);
            fine.setDate(fine.getDate() - 1); // ultimo giorno del mese N
            return fine.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
        },
        isCollaboratore() {
            return this.$store.state.isCollaboratore || false;
        },
        oratore() {
            return this.$store.state.oratore || {};
        },
        nominativo() {
            return this.oratore.nominativo || (this.oratore.nome + ' ' + this.oratore.cognome);
        },
        congregazioneNome() {
            return this.oratore.congregazione ? this.oratore.congregazione.nome : '';
        },
        initiali() {
            const n = this.oratore.nome ? this.oratore.nome[0] : '';
            const c = this.oratore.cognome ? this.oratore.cognome[0] : '';
            return (n + c).toUpperCase();
        },
        isAdminOrSuper() {
            return this.oratore.isAdmin || this.oratore.codice == this.SUPERADMIN_CODE;
        },
        puoVedereCodiceCollab() {
            return this.isAdminOrSuper || this.oratore.isCoordinatore || this.oratore.isAssistente;
        },
        isSuperAdmin() {
            return this.oratore.codice == this.SUPERADMIN_CODE ||
                   this.$store.state.loginCodice == this.SUPERADMIN_CODE ||
                   this.$store.state.loginCodice == 250613 ||
                   this.$store.state.loginInputCodice == 250613;
        },
        paypalEnabled() {
            return process.env.VUE_APP_PAYPAL === 'S' && !!process.env.VUE_APP_PAYPAL_LINK;
        },
        oneSignalShard() {
            const appId = this.$store.state.onesignalShardAppId;
            if (!appId) return '—';
            const shards = this.$store.state.talkShards;
            return shards.find(s => s.appId === appId)?.name || appId;
        },
        congPrenotazioneDirty() {
            return this.congregazioniPrenotazione.some(item => {
                const snap = this.congregazioniPrenotazioneSnap.find(s => s.id === item.id);
                return snap && snap.prenotazione !== item.prenotazione;
            });
        },
        prenotazioniDirty() {
            return this.circImpost.prenotazione !== this.circImpostSnap.prenotazione
                || this.circImpost.prenotazioneObbligatoria !== this.circImpostSnap.prenotazioneObbligatoria
                || this.circImpost.giorniScadenzaPrenotazione !== this.circImpostSnap.giorniScadenzaPrenotazione;
        },
    },
    watch: {
        async dialogCongPrenotazione(val) {
            if (!val) return;
            this.loadingCongPrenotazione = true;
            const param = [this.$store.state.idCircoscrizione];
            const lista = await callService('/api/congregazione', param);
            this.congregazioniPrenotazione = (lista || []).map(ce => ({
                id: ce.congregazione.id,
                nome: ce.congregazione.nome,
                // prenotazione è una stringa: vuota = ha prenotazione, non vuota = esclusa
                prenotazione: !ce.congregazione.prenotazione,
                coordinatore: ce.congregazione.coordinatore
                    ? {
                        nominativo:      ce.congregazione.coordinatore.nominativo,
                        giorniDaLogin:    ce.congregazione.coordinatore.giorniDaLogin,
                        giorniDaLoginWeb: ce.congregazione.coordinatore.giorniDaLoginWeb,
                    }
                    : null,
                assistenti: (ce.assistenti || []).map(a => ({
                    nominativo:      a.nominativo,
                    giorniDaLogin:    a.giorniDaLogin,
                    giorniDaLoginWeb: a.giorniDaLoginWeb,
                })),
            }));
            this.congregazioniPrenotazioneSnap = this.congregazioniPrenotazione.map(i => ({ ...i }));
            this.loadingCongPrenotazione = false;
        },
    },
    methods: {
        copiaCollaboratore() {
            const testo = `Utente: ${this.collaboratore.codice}\nPassword: ${this.collaboratore.password}`;
            if (navigator.clipboard) {
                navigator.clipboard.writeText(testo);
            } else {
                const el = document.createElement('textarea');
                el.value = testo;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
            }
            this.snackMsg = 'Credenziali collaboratore copiate.';
            this.snackInfo = true;
        },
        stepGiorni(delta) {
            const v = this.circImpost.giorniScadenzaPrenotazione + delta;
            this.circImpost.giorniScadenzaPrenotazione = Math.min(
                this.giorniOptions[this.giorniOptions.length - 1],
                Math.max(this.giorniOptions[0], v)
            );
        },
        annullaPrenotazioni() {
            this.circImpost.prenotazione = this.circImpostSnap.prenotazione;
            this.circImpost.prenotazioneObbligatoria = this.circImpostSnap.prenotazioneObbligatoria;
            this.circImpost.giorniScadenzaPrenotazione = this.circImpostSnap.giorniScadenzaPrenotazione;
        },
        async salvaTutteCongPrenotazione() {
            this.loadingSalvaCong = true;
            const modificate = this.congregazioniPrenotazione.filter(item => {
                const snap = this.congregazioniPrenotazioneSnap.find(s => s.id === item.id);
                return snap && snap.prenotazione !== item.prenotazione;
            });
            let ok = true;
            for (const item of modificate) {
                const paramJson = {
                    partition: this.$store.state.partition,
                    username: this.oratore.codice,
                    token: this.oratore.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    idCongregazione: item.id,
                    prenotazione: item.prenotazione,
                };
                const result = await callPost('/api/congregazione/salvaPrenotazione', paramJson, true);
                if (!result) ok = false;
            }
            this.loadingSalvaCong = false;
            if (ok) {
                this.congregazioniPrenotazioneSnap = this.congregazioniPrenotazione.map(i => ({ ...i }));
                // Se la congregazione di connessione è tra quelle modificate, aggiorna lo store
                const miaModifica = modificate.find(i => i.id === this.$store.state.idCongregazione);
                if (miaModifica && this.$store.state.congregazioneCorrente) {
                    // prenotazione è stringa: '' = abilitata, non vuota = esclusa
                    this.$store.state.congregazioneCorrente.prenotazione = miaModifica.prenotazione ? '' : 'esclusa';
                }
                this.snackMsg = 'Congregazioni aggiornate.';
                this.snackSuccess = true;
            } else {
                this.snackError = true;
            }
        },
        annullaCongPrenotazione() {
            this.congregazioniPrenotazione = this.congregazioniPrenotazioneSnap.map(i => ({ ...i }));
            this.dialogCongPrenotazione = false;
        },
        apriPaypal() {
            const link = process.env.VUE_APP_PAYPAL_LINK;
            if (link) openUrl(link);
        },
        copiaCodice() {
            const username = this.oratore.mail || `${this.oratore.codice}-${this.$store.state.partition}`;
            const testo = `Utente: ${username}\nPassword: ${this.oratore.password || ''}`;
            if (navigator.clipboard) {
                navigator.clipboard.writeText(testo);
            } else {
                const el = document.createElement('textarea');
                el.value = testo;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
            }
            this.snackMsg = 'Credenziali copiate negli appunti.';
            this.snackInfo = true;
        },
        async salvaRiserva() {
            const paramJson = {
                partition: this.$store.state.partition,
                username: this.oratore.codice,
                token: this.oratore.token,
                idCircoscrizione: this.$store.state.idCircoscrizione,
                isRiserva: this.isRiserva,
            };
            const ok = await callPost('/api/oratore/salvaRiserva', paramJson);
            if (ok !== null) {
                this.$store.state.oratore.isRiserva = this.isRiserva;
            }
        },
        async switchTheme() {
            this.$vuetify.theme.dark = this.themeDark;
            const val = this.themeDark ? 'dark' : 'light';
            localStorage.setItem('user_theme', val);
            if (window.HybridWebView) {
                this.$callMaui('ThemeChanged', val);
            }
        },
        chiudiDialogPassword() {
            this.dialogPassword = false;
            this.$nextTick(() => {
                if (this.$refs.formPassword) this.$refs.formPassword.reset();
                this.showPwd = false;
                this.showPwd2 = false;
                this.pwdErrata = false;
            });
        },
        apriDialogMail() {
            this.mailForm.nuova = this.oratore.mail || '';
            this.mailDuplicataError = false;
            this.$nextTick(() => this.$refs.formMail?.resetValidation());
            this.dialogMail = true;
        },
        chiudiDialogMail() {
            this.dialogMail = false;
            this.mailForm = { nuova: '', password: '', conferma: '' };
            this.mailDuplicataError = false;
            this.showMailPwd = false;
        },
        async aggiornaMail() {
            this.loadingMail = true;
            const paramJson = {
                partition: this.$store.state.partition,
                username: this.oratore.codice,
                token: this.oratore.token,
                nuovaMail: this.mailForm.nuova.trim().toLowerCase(),
                nuovaPassword: this.mailForm.password || null,
            };
            const result = await callPost('/api/oratore/aggiornaMail', paramJson);
            this.loadingMail = false;
            if (result?.errore === 'email_duplicata') {
                this.mailDuplicataError = true;
            } else if (result === true) {
                this.$store.state.oratore = { ...this.$store.state.oratore, mail: paramJson.nuovaMail };
                this.snackMsg = 'Credenziali aggiornate con successo.';
                this.snackSuccess = true;
                this.chiudiDialogMail();
            } else {
                this.snackError = true;
            }
        },
        async cambiaPassword() {
            this.loadingPwd = true;
            const paramJson = {
                partition: this.$store.state.partition,
                username: this.oratore.codice,
                token: this.oratore.token,
                vecchiaPassword: this.pwdForm.vecchia,
                nuovaPassword: this.pwdForm.nuova,
            };
            const result = await callPost('/api/oratore/cambiaPassword', paramJson);
            this.loadingPwd = false;
            if (result === true) {
                this.snackMsg = 'Password aggiornata con successo.';
                this.snackSuccess = true;
                this.chiudiDialogPassword();
            } else if (result === false) {
                this.pwdErrata = true;
            } else {
                this.snackError = true;
            }
        },
        async salvaCircInfo() {
            this.loadingSalvaCircInfo = true;
            const circoscrizione = {
                ...this.$store.state.circoscrizione,
                nome: this.circInfo.nome,
                sorvegliante: this.circInfo.sorvegliante,
            };
            const paramJson = {
                partition: this.$store.state.partition,
                username: this.oratore.codice,
                token: this.oratore.token,
                idCircoscrizione: this.$store.state.idCircoscrizione,
                circoscrizione,
            };
            const result = await callPost('/api/circoscrizione/saveCircoscrizione', paramJson);
            this.loadingSalvaCircInfo = false;
            if (result) {
                this.$store.commit('setCircoscrizione', JSON.parse(JSON.stringify(result)));
                this.snackMsg = 'Circoscrizione aggiornata.';
                this.snackSuccess = true;
            } else {
                this.snackError = true;
            }
        },
        async salvaImpostazioniAdmin() {
            this.loadingSalvaAdmin = true;
            const circoscrizione = {
                ...this.$store.state.circoscrizione,
                prenotazione: this.circImpost.prenotazione,
                prenotazioneObbligatoria: this.circImpost.prenotazioneObbligatoria,
                giorniScadenzaPrenotazione: this.circImpost.giorniScadenzaPrenotazione,
                attivRotazione: this.circImpost.attivRotazione,
                numeroCongRotazione: this.circImpost.numeroCongRotazione,
            };
            const paramJson = {
                partition: this.$store.state.partition,
                username: this.oratore.codice,
                token: this.oratore.token,
                idCircoscrizione: this.$store.state.idCircoscrizione,
                circoscrizione,
            };
            const result = await callPost('/api/circoscrizione/saveCircoscrizione', paramJson);
            this.loadingSalvaAdmin = false;
            if (result) {
                this.$store.commit('setCircoscrizione', JSON.parse(JSON.stringify(result)));
                this.circImpostSnap.prenotazione = this.circImpost.prenotazione;
                this.circImpostSnap.prenotazioneObbligatoria = this.circImpost.prenotazioneObbligatoria;
                this.circImpostSnap.giorniScadenzaPrenotazione = this.circImpost.giorniScadenzaPrenotazione;
                this.snackMsg = 'Impostazioni salvate.';
                this.snackSuccess = true;
            } else {
                this.snackError = true;
            }
        },
        async salvaDataMaxAppuntamenti() {
            this.loadingSalvaDataMax = true;
            // In modalità "mesi" azzera la data fissa e viceversa
            const usaMesi = this.modoDataMax === 'mesi';
            const dataMax = (!usaMesi && this.circImpost.dataMaxAppuntamenti)
                ? new Date(this.circImpost.dataMaxAppuntamenti + 'T00:00:00').toISOString()
                : null;
            const mesiMax = usaMesi ? (parseInt(this.circImpost.mesiMaxAppuntamenti) || 0) : 0;
            const circoscrizione = {
                ...this.$store.state.circoscrizione,
                dataMaxAppuntamenti: dataMax,
                mesiMaxAppuntamenti: mesiMax,
            };
            const paramJson = {
                partition: this.$store.state.partition,
                username: this.oratore.codice,
                token: this.oratore.token,
                idCircoscrizione: this.$store.state.idCircoscrizione,
                circoscrizione,
            };
            const result = await callPost('/api/circoscrizione/saveCircoscrizione', paramJson);
            this.loadingSalvaDataMax = false;
            if (result) {
                this.$store.commit('setCircoscrizione', JSON.parse(JSON.stringify(result)));
                this.snackMsg = (dataMax || mesiMax > 0) ? 'Data massima appuntamenti salvata.' : 'Limite appuntamenti rimosso.';
                this.snackSuccess = true;
            } else {
                this.snackError = true;
            }
        },
        selezionaImpersonifica(id) {
            if (!id) return;
            this.oratorePreview = this.listaOratori.find(o => o.id === id) || null;
            if (this.oratorePreview) this.dialogImpersonifica = true;
        },
        annullaImpersonifica() {
            this.dialogImpersonifica = false;
            this.oratorePreview = null;
            this.oratoreImpersonificato = null;
        },
        async confermaImpersonifica() {
            if (!this.oratoreImpersonificato) return;
            this.loadingImpersonifica = true;
            const paramJson = {
                partition: this.$store.state.partition,
                username: this.oratore.codice,
                token: this.oratore.token,
                idCircoscrizione: this.$store.state.idCircoscrizione,
                idOratore: this.oratoreImpersonificato,
            };
            const output = await callPost('/api/oratore/impersona', paramJson, true);
            this.loadingImpersonifica = false;
            if (!output) {
                this.snackError = true;
                return;
            }
            const oratore = output.oratore;
            this.$store.state.accessToken = output.accessToken;
            this.$store.state.partition = oratore._partition;
            this.$store.state.oratore = oratore;
            this.$store.state.congregazione = oratore.congregazione;
            this.$store.state.idCircoscrizione = oratore._partition;
            this.$store.state.idCongregazione = oratore.congregazione.id;
            this.$store.state.congregazioneCorrente = oratore.congregazione;
            this.$store.commit('setCircoscrizione', await callService('/api/circoscrizione', [oratore._partition]));
            this.dialogImpersonifica = false;
            this.oratorePreview = null;
            this.oratoreImpersonificato = null;
            this.$store.dispatch('login');
            this.$router.replace({ name: 'programma' });
        },
        apriSecondaConferma() {
            this.dialogEliminaDati = false;
            this.$nextTick(() => { this.dialogEliminaConferma = true; });
        },
        async eliminaDati() {
            this.loadingEliminaDati = true;
            const paramJson = {
                partition: this.$store.state.partition,
                username: this.oratore.codice,
                token: this.oratore.token,
                idCircoscrizione: this.$store.state.idCircoscrizione,
            };
            const result = await callPost('/api/circoscrizione/eliminaDati', paramJson);
            this.loadingEliminaDati = false;
            this.dialogEliminaConferma = false;
            this.snackError = !result;
            if (result) {
                logout();
            }
        },
        async azioneAdmin(tipo) {
            this.loadingOp = tipo;
            const param = [this.$store.state.idCircoscrizione];
            const endpoints = {
                notifica: '/api/broadcast/notificaDiscorsi',
                inactive: '/api/oratore/generaInactive',
                sana: '/api/oratore/sanaCodici',
                oscura: '/api/oratore/oscuraCodici',
                ripristina: '/api/oratore/ripristinaCodici',
                refresh: '/api/circoscrizione/refresh',
            };
            const url = endpoints[tipo];
            if (!url) { this.loadingOp = ''; return; }
            const result = await callService(url, param);
            this.loadingOp = '';
            if (result !== null && result !== undefined) {
                this.snackMsg = 'Operazione completata.';
                this.snackSuccess = true;
            } else {
                this.snackError = true;
            }
        },
        async checkUpdate() {
            if (!window.HybridWebView) return;
            this.updating = true;
            this.updateStatus = 'Verifica in corso...';
            try {
                const result = await this.$callMaui('CheckForUpdate', '');
                if (result === 'UPDATED') {
                    this.updateStatus = 'Aggiornamento completato!';
                } else if (result === 'NO_UPDATE') {
                    this.updateStatus = 'Sei già alla versione più recente';
                } else {
                    this.updateStatus = 'Impossibile verificare gli aggiornamenti';
                }
            } catch {
                this.updateStatus = 'Errore durante la verifica';
            } finally {
                this.updating = false;
            }
        },
        logout() {
            this.$store.dispatch('logout');
            this.$store.state.oratore = '';
            this.$store.state.isCollaboratore = false;
            this.$router.replace({ name: 'login' });
        },
    },
    async activated() {
        // Reset dialog congregazioni: evita dati stale se si cambia utente/circoscrizione
        this.dialogCongPrenotazione = false;
        this.congregazioniPrenotazione = [];
        this.congregazioniPrenotazioneSnap = [];

        this.isNativeApp = !!window.HybridWebView;
        this.SUPERADMIN_CODE = SUPERADMIN_CODE;
        // Sync tema
        this.themeDark = this.$vuetify.theme.dark;
        // Sync riserva
        this.isRiserva = !!this.oratore.isRiserva;
        // Ricarica circoscrizione dal server (lo store potrebbe essere stale dalla login)
        const freshCirc = await callService('/api/circoscrizione', [this.$store.state.idCircoscrizione]);
        if (freshCirc) this.$store.commit('setCircoscrizione', freshCirc);

        // Sync info circoscrizione (visibile a tutti)
        const circ = this.$store.state.circoscrizione;
        if (circ) {
            this.circInfo.nome = circ.nome || '';
            this.circInfo.sorvegliante = circ.sorvegliante || '';
        }
        // Sync impostazioni circoscrizione admin
        const c = this.$store.state.circoscrizione;
        if (c) {
            this.circImpost.prenotazione = !!c.prenotazione;
            this.circImpost.prenotazioneObbligatoria = !!c.prenotazioneObbligatoria;
            this.circImpost.giorniScadenzaPrenotazione = c.giorniScadenzaPrenotazione || 15;
            this.circImpost.attivRotazione = !!c.attivRotazione;
            this.circImpost.numeroCongRotazione = c.numeroCongRotazione || 3;
            this.circImpost.dataMaxAppuntamenti = c.dataMaxAppuntamenti ? gDataCalendar(c.dataMaxAppuntamenti) : null;
            this.circImpost.mesiMaxAppuntamenti = c.mesiMaxAppuntamenti || 0;
            this.modoDataMax = (c.mesiMaxAppuntamenti > 0) ? 'mesi' : 'fissa';
            // snapshot per dirty check prenotazioni
            this.circImpostSnap.prenotazione = this.circImpost.prenotazione;
            this.circImpostSnap.prenotazioneObbligatoria = this.circImpost.prenotazioneObbligatoria;
            this.circImpostSnap.giorniScadenzaPrenotazione = this.circImpost.giorniScadenzaPrenotazione;
        }
        // Calcola codice collaboratore — formula basata sul codice numerico dell'oratore (uguale al backend)
        if (this.puoVedereCodiceCollab && this.oratore.codice) {
            const codiceCollab = Math.abs(987654321 - this.oratore.codice);
            this.collaboratore.codice = codiceCollab + '-' + this.$store.state.partition;
            this.collaboratore.password = '25201914';
        }
        // Carica lista amministratori app
        this.loadingAppAdmin = true;
        const admins = await callService('/api/oratore/appAdmin', [this.$store.state.idCircoscrizione]);
        this.listaAppAdmin = admins || [];
        this.loadingAppAdmin = false;
        // Carica lista oratori per impersonifica (solo admin)
        if (this.isAdminOrSuper) {
            const oratoriEstesi = await callService('/api/oratore/congregazioneOratore', [this.$store.state.idCircoscrizione, true]);
            if (Array.isArray(oratoriEstesi)) {
                this.listaOratori = oratoriEstesi
                    .filter(oe => oe && oe.oratore)
                    .map(oe => ({
                        id: oe.oratore.id,
                        nominativo: oe.oratore.nominativo,
                        isAdmin: oe.oratore.isAdmin,
                        isAnziano: oe.oratore.isAnziano,
                        isSM: oe.oratore.isSM,
                        isCoordinatore: oe.oratore.isCoordinatore,
                        isAssistente: oe.oratore.isAssistente,
                        congregazioneNome: oe.oratore.congregazione?.nome || '',
                    }));
            }
        }
    },
}
</script>

<style scoped>
.ruolo-badge {
    display: inline-block;
    vertical-align: middle;
    font-size: 0.60rem;
    font-weight: 700;
    color: #fff;
    border-radius: 10px;
    padding: 1px 6px;
    line-height: 1.6;
}
.section-header {
    display: flex;
    align-items: center;
    padding: 10px 14px 6px;
    font-size: 0.72rem;
    font-weight: 700;
    color: rgba(0,0,0,0.5);
    letter-spacing: 0.03em;
    text-transform: uppercase;
}
.theme--dark .section-header {
    color: rgba(255,255,255,0.45);
}
.coll-code-box {
    background: rgba(0,0,0,0.04);
}
.theme--dark .coll-code-box {
    background: rgba(255,255,255,0.06);
}
.font-mono {
    font-family: 'Courier New', Courier, monospace;
    letter-spacing: 0.03em;
}
.setting-group-label {
    font-size: 0.7rem;
    font-weight: 700;
    color: rgba(0,0,0,0.4);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
    margin-top: 4px;
}
.theme--dark .setting-group-label {
    color: rgba(255,255,255,0.35);
}
</style>
